import React, { useState, useEffect } from 'react';
import { Container, Button, Form, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import axios from '../../../api/axios';
import { useDebounce } from '../../../hooks/useDebounce';
import { User, PaginationData, SortConfig } from '../../../types';
import { getSortIcon, renderPagination } from '../../../utils/Utils';
import AdminHeader from '../layout/AdminHeader';
import AdminNavigation from '../layout/AdminNavigation';
import './AdminClientList.css';

const AdminClientList: React.FC = () => {
  const [clients, setClients] = useState<PaginationData>({} as PaginationData);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showDeleted, setShowDeleted] = useState<boolean>(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'name', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
  }, [currentPage, sortConfig, debouncedSearchTerm, showDeleted]);


  const fetchClients = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        sort: sortConfig.key,
        direction: sortConfig.direction,
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
        ...(showDeleted && { show_deleted: 'true' })
      });

      const response = await axios.get(`/admin/users?${params}`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setError('クライアント一覧の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: string): void => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleShowDeletedChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setShowDeleted(e.target.checked);
    setCurrentPage(1);
  };

  const handleDelete = async (userId: number, userName: string) => {
    if (!window.confirm(`${userName}の情報を削除してもよろしいですか？`)) {
      return;
    }

    try {
      await axios.delete(`/admin/users/${userId}`);
      alert(`${userName}の情報が削除されました。`);
      fetchClients();
    } catch (error: any) {
      alert(error.response?.data?.message || '削除に失敗しました。');
    }
  };

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  return (
    <div className="dashboard-container">
      <AdminHeader />

      <Container>

        <div className="navigation-container">
          <AdminNavigation users={!showDeleted ? clients.total : undefined} />
          <div className="search-container">
            <div className="search-input-container">
              <Form>
              <Form.Control
                type="text"
                placeholder="クライアント名／担当者名／メールアドレス"
                value={searchTerm}
                onChange={handleSearch}
                className="search-input"
              />
              </Form>
            </div>
            <div className="create-button-container">
              <Button 
                variant="success" 
                className="create-button create-client"
                onClick={() => navigate('/admin/clients/new')}
              >
                新規クライアント作成
              </Button>
            </div>
          </div>
        </div>

        <div className="search-deleted-container">
          <Form.Check
            type="checkbox"
            id="show-deleted-clients"
            label="削除されたデータのみ表示"
            checked={showDeleted}
            onChange={handleShowDeletedChange}
          />
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        <div className="candidates-table-container">
          <table className="candidates-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('name')} className="sortable">
                  クライアント名 {getSortIcon('name', sortConfig)}
                </th>
                <th onClick={() => handleSort('id')} className="sortable text-center">
                  クライアントID {getSortIcon('id', sortConfig)}
                </th>
                <th onClick={() => handleSort('contact_person')} className="sortable">
                  担当者名 {getSortIcon('contact_person', sortConfig)}
                </th>
                <th onClick={() => handleSort('phone')} className="sortable">
                  電話番号 {getSortIcon('phone', sortConfig)}
                </th>
                <th onClick={() => handleSort('email')} className="sortable">
                  メールアドレス {getSortIcon('email', sortConfig)}
                </th>
                <th onClick={() => handleSort('candidates_count')} className="sortable text-center">
                  候補者数 {getSortIcon('candidates_count', sortConfig)}
                </th>
                <th className="text-center">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center">
                    <div className="blank-form-container">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-3">情報を読み込んでいます...</p>
                    </div>
                  </td>
                </tr>
              ) : (
              clients.data && clients.data.length > 0 ? (
                clients.data.map((user: User) => (
                  <tr key={user.id}>
                    <td>{user.name}</td>
                    <td className="text-center">{user.id.toString().padStart(5, '0')}</td>
                    <td>{user.contact_person || '-'}</td>
                    <td>{user.phone || '-'}</td>
                    <td>{user.email}</td>
                    <td className="text-center">{user.candidates_count}</td>
                    <td className="text-center">
                    {((user.deleted_at || '') === '') && (
                      <>
                        <Button 
                          className="detail-button me-1"
                          onClick={() => navigate(`/admin/clients/${user.id}`)}
                        >
                          詳細
                        </Button>
                        <Button 
                          className="delete-button-icon"
                          onClick={() => handleDelete(user.id, user.name)}
                          title="削除"
                        >
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </Button>
                      </>
                    )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center">該当するデータはありません。</td>
                </tr>
              )
            )}
            </tbody>
          </table>
        </div>

        {clients.last_page > 1 && renderPagination(currentPage, clients.last_page, paginate)}
      </Container>
    </div>
  );
};

export default AdminClientList;
